/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';
import { jsx } from '@compiled/react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import type { ItemsConfig } from '@atlassian/global-side-navigation/common/types';
import { PlaceholderWithNativeSuspense } from '@atlassian/jira-placeholder/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';

const SIDEBAR_TEST_ID = 'page-layout.sidebar';
const EMPTY_CONFIG_OBJ = {} as const;
const useMatchPath = createRouterSelector((state) => state.match.path);

export function AnonymousSidebar(): JSX.Element {
	const itemConfig = useItemConfig();

	return (
		<UFOSegment name="sidebar.anonymous">
			<JSErrorBoundary
				fallback="flag"
				id="sidebars-anonymous"
				packageName="jira-navigation-apps-sidebars-anonymous"
			>
				<PlaceholderWithNativeSuspense name="nav4-ignore.sidebar.anonymous">
					<GlobalSideNavigation
						app="jira"
						appLabel="Jira"
						customizationStore="local"
						disableMenuCustomization
						forYou={EMPTY_CONFIG_OBJ}
						isShortcutsStatic
						items={itemConfig}
						testId={SIDEBAR_TEST_ID}
					/>
				</PlaceholderWithNativeSuspense>
			</JSErrorBoundary>
		</UFOSegment>
	);
}

function useItemConfig(): ItemsConfig {
	const { formattedMessage: projectsMessage, icon: projectsIcon } =
		useConfigurableMessageAndIconMap(L1_MENU_ID.PROJECTS);
	const { formattedMessage: dashboardsMessage, icon: dashboardsIcon } =
		useConfigurableMessageAndIconMap(L1_MENU_ID.DASHBOARDS);
	const { formattedMessage: filtersMessage, icon: filtersIcon } = useConfigurableMessageAndIconMap(
		L1_MENU_ID.FILTERS,
	);
	const path = useMatchPath();

	return useMemo(
		() => ({
			[L1_MENU_ID.PROJECTS]: {
				elemBefore: projectsIcon,
				href: '/jira/projects',
				isSelected: path.startsWith('/jira/projects'),
				label: projectsMessage,
				type: 'link',
			},
			[L1_MENU_ID.FILTERS]: {
				elemBefore: filtersIcon,
				href: '/jira/filters',
				isSelected: path.startsWith('/jira/filters'),
				label: filtersMessage,
				type: 'link',
			},
			[L1_MENU_ID.DASHBOARDS]: {
				elemBefore: dashboardsIcon,
				href: '/jira/dashboards',
				isSelected: path.startsWith('/jira/dashboards'),
				label: dashboardsMessage,
				type: 'link',
			},
		}),
		[
			dashboardsIcon,
			dashboardsMessage,
			filtersIcon,
			filtersMessage,
			path,
			projectsIcon,
			projectsMessage,
		],
	);
}
